import { View } from "extensions/marionette";
import { Backbone } from "extensions/backbone";
import { Highcharts } from "extensions/highcharts";
import { toast } from "extensions/toast";
import { _ } from "extensions/underscore";
import $ from "jquery";
import isPlainObject from "lodash-es/isPlainObject";
import moment from "moment";
import { captureException } from "extensions/sentry";

import { store } from "common/store";
import { toHex } from "common/colors";

import { Loader } from "common/components/loader";
import { walk } from "common/components/utils/walk";

import { Draggah } from "common/components/charts/draggah";

import "./chart.scss";

const CHART_DEFAULTS = {
    marker: {
        lineWidth: 2,
        fillColor: toHex("canvas-white"),
        lineColor: null,
        symbol: "circle",
        radius: 3
    },
    lineWidth: 3
};

const ChartEmptyView = View.extend({
    className: "chart-empty-view",
    template: () => "There is no data available for the selected date range"
});

export const Chart = View.extend({
    fixedTooltip: false,
    showEmptyViewIfNoData: true,
    displayLoader: true,
    range: "day",
    
    className() {
        const base = ["smartChart"];
        
        if (this.getOption("fixedTooltip")) {
            base.push("fixed-tooltip-chart");
        }
        
        return base.join(" ");
    },
    template: () => "",
    emptyView: ChartEmptyView,
    constructor: function constructor() {
        View.apply(this, arguments);
        this.chartOptions = {
            title: {
                text: ""
            },
            yAxis: {
                title: {
                    text: null
                }
            },
            tooltip: {
                useHTML: true,
                backgroundColor: "rgba(0, 0, 0, 0.75)",
                borderRadius: 10,
                borderColor: null,
                borderWidth: 0,
                shadow: false,
                headerFormat: "<header>{point.key}</header>",
                pointFormat: "<div class='point-data'><span class='series-name'>{series.name}:</span>&nbsp;<span class='point-y'>{point.y}</span></div>",
                style: {
                    color: "#fff",
                    fontSize: "14px",
                    padding: "8px"
                },
                valueDecimals: 0
            },
            plotOptions: {
                area: {
                    fillOpacity: 0.2,
                    lineWidth: CHART_DEFAULTS.lineWidth,
                    marker: CHART_DEFAULTS.marker
                },
                line: {
                    lineWidth: CHART_DEFAULTS.lineWidth,
                    marker: CHART_DEFAULTS.marker
                },
                series: {
                    states: {
                        hover: {
                            brightness: -0.15 // darkens the graph data point on hover instead of default lightening it
                        }
                    }
                }
            }
        };
    },
    
    /**
     * Deep extend chart extra option.
     * @param {object} opts object to add to chart opts
     * @returns {object} chart extra options
     */
    extendChartOptions(opts) {
        this.chartOptions = $.extend(true, {}, this.chartOptions, opts || {});
        return this.chartOptions;
    },
    
    /**
     * Add draggah to chart object and store Draggah instance under chart instance
     * @param chartObject
     * @private
     */
    _addDraggah(chartObject) {
        const draggah = this.getOption("draggah");
        if (draggah && draggah.enabled) {
            // Destroy draggah if there is one already
            if (this.draggah) {
                this.draggah.destroy();
            }
            
            // Create Draggah object and assign it to the component
            this.draggah = new Chart.DateZoomer();
            
            // If there are any events to listen to then bind on those
            // There should be no leaks as it stops listening to events at Draggah destructor
            Object.entries(draggah.events).forEach(([event, handler]) => {
                this.draggah.on(event, handler);
            });
            
            // Associate the Selection event with the Draggah
            chartObject.chart.events.selection = this.draggah.onSelection;
            
            // Allow only zoom on x-Axis as the draggah is enabled
            chartObject.chart.zoomType = "x";
            
            // Destroy draggah when view is destroyed
            this.on("destroy", function() {
                this.draggah.destroy();
            });
        }
    },
    
    buildChartObject(series) {
        // Initial object
        let chartObject = {
            chart: {
                events: {}
            },
            series: [],
            tooltip: {}
        };
        
        // Add type if needed
        // eslint-disable-next-line eqeqeq
        if (this.getOption("chartType") != null) {
            chartObject.chart.type = this.getOption("chartType");
        }
        
        this._addDraggah(chartObject);
        
        // Add element where to render the chart
        chartObject.chart.renderTo = this.getChartContainer()[0];
        
        // Add series if they exist
        if (series || this.getOption("chartSeries")) {
            chartObject.series = series || this.getOption("chartSeries");
        }
        
        const valueType = this.getOption("valueType");
        if (valueType) {
            this.addTooltipFormatter(valueType);
        }
        
        // Change y-min value if set by the chart config
        if (this.getOption("dynamicMinY") === true && !_.isArray(chartObject.yAxis)) {
            $.extend(true, chartObject, {
                yAxis: {
                    min: this.findMinYValue(chartObject.series)
                }
            });
        }
        
        // Extend with constructor chart options first, then add instance options
        chartObject = $.extend(true, {}, chartObject, this.chartOptions || {}, this.options.chartOptions || {});
        
        // Check if Axis labels require special formatting
        chartObject = this.formatYAxisLabels(chartObject);
        
        return chartObject;
    },
    
    formatYAxisLabels(chartObject) {
        const self = this;
        const format = function(axis) {
            if (axis.valueType === "amount") {
                axis.labels = axis.labels || {};
                
                axis.labels.formatter = function() {
                    const value = this.axis.defaultLabelFormatter.apply(this, arguments);
                    const currency = self.getCurrentCurrency();
                    
                    // Don't bother to add full currency code, check if it has a symbol
                    if (currency.symbol) {
                        return currency.symbol + value;
                    }
                    return value;
                };
            }
            return axis;
        };
        
        if (_.isArray(chartObject.yAxis)) {
            chartObject.yAxis = _.map(chartObject.yAxis, format);
            // eslint-disable-next-line eqeqeq
        } else if (chartObject.yAxis != null) {
            chartObject.yAxis = format(chartObject.yAxis);
        }
        
        return chartObject;
    },
    
    findMinYValue(series) {
        let chartMinYValue;
        
        _.each(series, (seria) => {
            _.each(seria.data, (point) => {
                // Set initial value if undefined
                if (chartMinYValue === void 0) {
                    chartMinYValue = point[1];
                }
                chartMinYValue = Math.min(chartMinYValue, point[1]);
            });
        });
        
        return chartMinYValue;
    },
    
    getTooltipFormatter(valueType) {
        const self = this;
        let tooltipFormatter;
        
        if (valueType === "amount") {
            tooltipFormatter = function() {
                const xyArr = [];
                _.each(this.points, (point) => {
                    xyArr.push(`<span style="color:${point.series.color}">\u25CF</span> ${
                        point.series.name}: <b>${self.formatAmountWithCurrentCurrency(point.y)}</b>`);
                });
                return xyArr.join("<br/>");
            };
        } else {
            tooltipFormatter = function() {
                const xyArr = [];
                _.each(this.points, (point) => {
                    xyArr.push(`<span style="color:${point.series.color}">\u25CF</span> ${
                        point.series.name}: <b>${self.formatNumber(point.y)}</b>`);
                });
                return xyArr.join("<br/>");
            };
        }
        
        return tooltipFormatter;
    },
    
    /**
     * Add amount or count tooltip formatter
     * @param {string} valueType Either `amount` or `count`
     * @private
     */
    addTooltipFormatter(valueType) {
        this.extendChartOptions({
            tooltip: {
                formatter: this.getTooltipFormatter(valueType)
            }
        });
    },
    
    isTrendlineChart() {
        const chartType = this.getOption("chartType");
        return ["AreaLine", "Line"].includes(chartType);
    },
    
    _prepareData(structure) {
        const processData = this.getOption("processData");
        let data;
        
        if (typeof processData === "function") {
            data = processData.call(this, structure);
        }
        
        return data instanceof Backbone.Collection ? data.toJSON() : data;
    },
    
    _computeSeriaPoint(seria) {
        return (dataPoint, index) => { // Context is the view
            // if chart requires seria point to be represented by an object (e.g. `map` or `pie`) then use mapObject function
            const map = seria.object ? _.mapObject : _.map;
            
            return map(seria.pointFormat, (seriaPoint) => {
                if (typeof seriaPoint === "function") {
                    return seriaPoint.call(this, dataPoint, index);
                }
                
                if (seriaPoint === "date_time") {
                    try {
                        return moment.utc(dataPoint.date_time).valueOf();
                    } catch {
                        captureException(new Error("Cannot convert date_time to moment instance"));
                    }
                }
                
                return walk(dataPoint, seriaPoint);
            });
        };
    },
    
    _computeSingleSeria(data) {
        const self = this;
        return function(seria) {
            const obj = {};
            if (seria.name) {
                obj.name = seria.name;
            }
            if (seria.type) {
                obj.type = seria.type;
            }
            if (seria.pointFormat) {
                obj.data = _.map(data, self._computeSeriaPoint(seria), this);
            }
            
            switch (seria.valueType) {
            case "amount": {
                obj.tooltip = {
                    valuePrefix: _.bind(function() {
                        return this.getCurrentCurrency().symbol;
                    }, this),
                    valueSuffix: _.bind(function() {
                        return ` ${this.getCurrentCurrency().currency}`;
                    }, this),
                    valueDecimals: store.getState().config.formatting.money.precision
                };
            
                break;
            }
            case "percentage": {
                obj.tooltip = {
                    valueSuffix: "%",
                    valueDecimals: 2
                };
            
                break;
            }
            case "count": {
                obj.tooltip = {};
            
                break;
            }
            default: { break; }
            }
            
            // Highcharts like sorted points! Sort points by first item
            if (!seria.sorted && self.isTrendlineChart()) {
                obj.data = _.sortBy(obj.data, (point) => point[0]);
            }
            
            if (seria.extend && _.isObject(seria.extend)) {
                _(obj).extend(seria.extend);
            }
            
            if (isPlainObject(self.getOption("viewSeriaModifier"))) {
                Object.assign(obj, self.getOption("viewSeriaModifier"));
            }
            
            return obj;
        };
    },
    
    _computeSeries(data) {
        let series = this.getOption("series");
        
        if (series) {
            if (_.isFunction(series)) {
                series = series(data);
            }
            
            // Hate English word series. Russian and Italian have singular "seria"
            return _.map(series, this._computeSingleSeria(data), this);
        }
        return data;
    },
    
    buildChartSeries(structure) {
        const data = this._prepareData(structure);
        const series = this._computeSeries(data);
        
        // Store data in the object for lookups
        this.data = data;
        
        return series;
    },
    
    /**
     * Render empty view. Used when chart doesn't have any data
     */
    _showEmptyView() {
        const EmptyView = this.getOption("emptyView");
        this._emptyView = new EmptyView();
        this._emptyView.render();
        this._emptyView.$el.appendTo(this.el);
        this._showingEmptyView = true;
    },
    
    /**
     * Hide empty view if it's shown
     */
    hideEmptyView() {
        if (this._showingEmptyView) {
            this._emptyView.destroy();
            delete this._showingEmptyView;
        }
    },
    
    /**
     * Check if chart data is empty
     */
    isNotEmpty(series) {
        return _.some(series, (seria) => seria.data && seria.data.length > 0);
    },
    
    getChartContainer() {
        if (this.getOption("fixedTooltip")) {
            return this.$(".chart");
        }
        return this.$el;
    },
    
    updateTooltipContainer(html) {
        this.el.querySelector(".fixedTooltip").innerHTML = html;
    },
    
    setCustomLegend(html) {
        this.el.querySelector(".legend").innerHTML = html;
    },
    
    renderTooltipContainer() {
        if (this.getOption("fixedTooltip")) {
            this.$el.empty().append("<div class='chart-body'><div class='legend'></div><div class='chart'></div></div><div class='fixedTooltip'></div>");
        }
    },
    
    getChartConstructor() {
        return Highcharts.Chart;
    },
    
    reflow() {
        if (this.chart) {
            this.chart.reflow();
        }
    },
    
    setDimensions() {
        if (this.getOption("height")) {
            this.getChartContainer().css("height", this.getOption("height"));
        }
        if (this.getOption("width")) {
            this.getChartContainer().css("width", this.getOption("width"));
        }
    },
    
    onRender() {
        this.renderTooltipContainer();
        
        const structure = this.model || this.collection; // Either model or collection
        const series = this.buildChartSeries(structure);
        let chartObject = this.buildChartObject(series);
        
        this.setDimensions();
        
        if (this.isNotEmpty(series)) {
            this.hideLoader();
            const chartObjectModifier = this.getOption("processChartObjectBeforeRender");
            if (chartObjectModifier && _.isFunction(chartObjectModifier)) {
                chartObject = chartObjectModifier.call(this, chartObject);
            }
            
            const Constructor = this.getChartConstructor();
            this.chart = new Constructor(chartObject);
            
            // Add raw data to the chart
            this.chart.raw = this.data;
            
            // Set a reference to the chart inside the draggah
            if (this.getOption("draggah") && this.getOption("draggah").enabled) {
                // Pass range
                this.chart.range = this.getOption("range");
                
                // Pass a reference to draggah
                this.draggah.chart = this.chart;
            }
            
            // Call instance afterChartRender function
            if (typeof this.getOption("afterChartRender") === "function") {
                this.getOption("afterChartRender").call(this, this.chart);
            }
            
            // Call subclass (and event) afterChartRender function
            this.triggerMethod("after:chart:render", this.chart, this);
        } else if (_.result(structure, "isFetched", true) && this.getOption("showEmptyViewIfNoData")) {
            this._showEmptyView();
        } else {
            this.showLoader();
        }
    },
    onDestroy() {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
        this.chartObject = null;
        this.unbindDomEvents();
        this.hideLoader();
        this.hideEmptyView();
    },
    
    /**
     * Show loader inside chart element
     */
    showLoader() {
        if (this.getOption("displayLoader")) {
            Loader.addTo(this.getChartContainer());
            this._loaderShown = true;
        }
    },
    
    /**
     * Hide loader from the chart element
     */
    hideLoader() {
        if (this.getOption("displayLoader") && this._loaderShown) {
            Loader.removeFrom(this.$el);
            this._loaderShown = false;
        }
        return this;
    },
    
    /**
     * Bind various events happening with chart elements
     * Common cases implemented by this method. Custom are by subclasses.
     */
    bindDomEvents() {},
    
    /**
     * Unbind events from chart's elements
     * Common cases implemented by this method. Custom are by subclasses.
     */
    unbindDomEvents() {},
    
    /**
     * Final touch on chart object before it get rendered (re-rendered) in the DOM.
     * Subclasses or instances should reimplement this object
     * @param {object} chartObject Object to be passed into Highcharts for instantiation
     * @returns {object} modified chart object
     */
    processChartObjectBeforeRender(chartObject) {
        return chartObject;
    },
    
    showTooltipOfLastPoint() {
        // eslint-disable-next-line eqeqeq
        if (this.chart != null) {
            const tooltipToRefresh = _.chain(this.chart.series).map((seria) => seria.visible && _.last(seria.data)).compact().value();

            if (tooltipToRefresh.length) {
                this.chart.tooltip.refresh(tooltipToRefresh);
            }
        }
    },
    
    addThresholdBand(thresholdInfo) {
        // eslint-disable-next-line eqeqeq
        if (this.chart != null) {
            const plotBand = {
                color: toHex("canvas-mid"),
                from: 0,
                to: thresholdInfo.threshold
            };
            const plotLine = { // mark the weekend
                color: toHex("gray-mid"),
                width: 2,
                value: thresholdInfo.threshold,
                dashStyle: "dash"
            };
            const axis = thresholdInfo.axis === "y" ? this.chart.yAxis[thresholdInfo.axisIndex] : this.chart.xAxis[thresholdInfo.axisIndex];
            axis.addPlotBand(plotBand);
            axis.addPlotLine(plotLine);
        }
    },
    
    doExport(options, chartOptions) {
        if (this._showingEmptyView) {
            toast.error("Cannot export chart since there is no data available for the selected date range");
            return;
        }
        
        this.chart.exportPazienChart(options, chartOptions);
    },
    
    hideAllSeries() {
        if (this.chart) {
            this.hideEmptyView();
            _.each(this.chart.series, (seria) => {
                seria.setVisible(false, false);
            });
        }
    },
    
    listenToCollectionEvents() {
        if (this.collection instanceof Backbone.Collection) {
            this.listenTo(this.collection, "before:fetched", () => {
                // Hide all series
                this.hideAllSeries();
                this.showLoader();
            });
            this.listenTo(this.collection, "fetched", this.render);
            this.listenTo(this.collection, "filtered", this.render);
        }
    }
}, {
    DateZoomer: Draggah
});
